/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 *
 * Form Validation State Hook
 *
 * Reusable hook for managing hasAttemptedSubmit state pattern.
 * This pattern ensures validation errors are only shown after a user attempts to submit,
 * improving UX by not showing errors on initial render or blur.
 */

"use client";

import { useCallback, useState } from "react";

export interface UseFormValidationStateReturn {
  /**
   * Whether the user has attempted to submit the form
   */
  hasAttemptedSubmit: boolean;

  /**
   * Marks that the user has attempted to submit the form.
   * Call this at the start of your submit handler.
   */
  handleSubmitAttempt: () => void;

  /**
   * Resets the validation state.
   * Call this when opening/closing modals or resetting the form.
   */
  reset: () => void;
}

/**
 * Hook for managing form validation state with hasAttemptedSubmit pattern
 *
 * @example
 * ```tsx
 * const { hasAttemptedSubmit, handleSubmitAttempt, reset } = useFormValidationState();
 *
 * const handleSubmit = (e: React.FormEvent) => {
 *   e.preventDefault();
 *   handleSubmitAttempt(); // Mark that user attempted submit
 *
 *   if (!isFormValid) return;
 *   // ... submit logic
 * };
 *
 * useEffect(() => {
 *   if (isOpen) {
 *     reset(); // Reset when modal opens
 *   }
 * }, [isOpen, reset]);
 * ```
 */
export function useFormValidationState(): UseFormValidationStateReturn {
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);

  const handleSubmitAttempt = useCallback(() => {
    setHasAttemptedSubmit(true);
  }, []);

  const reset = useCallback(() => {
    setHasAttemptedSubmit(false);
  }, []);

  return {
    hasAttemptedSubmit,
    handleSubmitAttempt,
    reset,
  };
}
